import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import moment from 'moment';

// Controls
import { Box, Card, CardContent, CardHeader, Grid, Typography } from '@mui/material'

// Components
import { Loading, Button, IconButton, Tooltip } from "@gpstrackit/gtc-react-controls";
import SummaryItem from './modals/summaryItem';
import GadgetDateInfo from '../gadgetDateInfo';

// Actions
import { setDriverPerformanceMainTab } from "Modules/driverPerformance/views/main/redux/slice";
import { getGadgetData } from '../../dashboard/redux/slice';

// Styles
import './index.scss';

export const SummaryGadget = ({ gadgetkey, name, filters, configHandler, customFilters, containerDimensions, onClick }) => {
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const navigate = useNavigate();
    const isEventHighlights = gadgetkey === 'EventHighlights';

    // Redux states
    const { gadgetsData } = useSelector(state => state.dashboardRedux);

    const gadgetData = gadgetsData[gadgetkey] ? gadgetsData[gadgetkey] : { loading: false, data: { items: [], total: 0 } };
    const { loading, data } = gadgetData;

    // Memoize filters to avoid unnecessary re-renders when reference changes but content is the same
    const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)]);

    useEffect(() => {
        const currentFilters = isEventHighlights
            ? { ...memoizedFilters, drivers: [] }
            : { ...memoizedFilters };
        dispatch(getGadgetData({ gadgetkey, page: 1, pageSize: 25, filters: currentFilters }));
    }, [memoizedFilters])

    const displayData = data?.items || [];
    const viewDataSummary = () => {
        switch (gadgetkey) {
            case 'CasesCoachingSummary':
                return (
                    <Grid container spacing={1} className="summary-gadget__grid-container" style={{ display: 'contents', justifyContent: 'center' }}>
                        <Grid key={`${gadgetkey}-left`} item className="summary-gadget__grid-item">
                            <SummaryItem label={messages['cases_coaching_summary_pending']} value={displayData.pendingCount} />
                            <SummaryItem label={messages['cases_coaching_summary_evaluation']} value={displayData.evaluationCount} />
                            <SummaryItem label={messages['cases_coaching_summary_completed']} value={displayData.completedCount} />
                            <SummaryItem label={messages['cases_coaching_summary_avgDuration']} value={displayData.avgDurationDays} />
                            <SummaryItem label={messages['cases_coaching_summary_pISuccessRat']} value={displayData.piSuccessRate} suffix="%" />
                            <br />
                            <SummaryItem label={messages['cases_coaching_summary_eligibleDrivers']} value={displayData.eligibleDriversCount} style={{ marginBottom: 20 }} />
                        </Grid>
                        <Grid key={`${gadgetkey}-right`} item className="summary-gadget__grid-item">
                            <div className="summary-gadget__info summary-gadget__card-content_custom" style={{ marginBottom: 6 }}>
                            </div>
                            <div className="summary-gadget__info summary-gadget__card-content_custom" style={{ marginBottom: 6 }}>
                            </div>
                            <div className="summary-gadget__info summary-gadget__card-content_custom" style={{ marginBottom: 6 }}>
                            </div>
                            <div className="summary-gadget__info summary-gadget__card-content_custom" style={{ marginTop: 50 }}>
                                <span style={{ color: '#8b8b8b' }}>({messages['cases_coaching_summary_daysToComplete']})</span>
                            </div>
                            <br />
                            <div className="summary-gadget__info summary-gadget__card-content_custom" style={{ marginTop: 20 }}>
                                <Button
                                    icon=""
                                    onClick={(e) => {
                                        e.preventDefault();
                                        dispatch(setDriverPerformanceMainTab(3));
                                        navigate(`/app/driverPerformance/main?showCreateModal=true`);
                                    }}
                                    variant="secondary"
                                >{messages['cases_coaching_summary_createNew']}
                                </Button>
                            </div>
                        </Grid>
                    </Grid>
                );
                break;
            default:
                return (
                    <Grid
                        container
                        spacing={1}
                        className="summary-gadget__grid-container"
                        style={{
                            margin: gadgetkey === 'EventHighlights' ? 0 : undefined,
                            width: gadgetkey === 'EventHighlights' ? '100%' : undefined
                        }}
                    >
                        {displayData?.length > 0 ? displayData.map((metric, index) => {
                            let name = metric?.metric || ''
                            let label = messages[`driver_performance_${name?.replace(/\s+/g, "")}`] || name;
                            let noLink = (name.includes("Unassigned Driver") || metric.qtty == 0);
                            return (
                                <Grid key={index} item xs={12} sm={2} md={2} xl={2} className="summary-gadget__grid-item" style={{ minWidth: 0 }}>
                                    <Box className="summary-gadget__content-box" style={{ minWidth: 0 }}>
                                        <div onClick={() => {
                                            (onClick && !noLink) && onClick(gadgetkey, {
                                                value: metric.qtty,
                                                label: metric?.metric,
                                                filters: memoizedFilters
                                            }, dispatch, navigate)
                                        }} className={(noLink) ? "summary-gadget__metric-value-no-link" : "summary-gadget__metric-value"}>{metric.qtty || 0}</div>
                                        <div className="summary-gadget__metric-label-wrapper" style={{ minWidth: 0, flex: 1 }}>
                                            <Tooltip title={label} placement="top">
                                                <div className="summary-gadget__metric-label" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
                                            </Tooltip>
                                        </div>
                                    </Box>
                                </Grid>
                            )
                        }) : <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', marginTop: 30, color: '#8b8b8b' }}>{messages['risk_no_data'] || 'No data'}</div>}
                    </Grid>
                );
        }
    };

    return (
        <Card className="summary-gadget" style={{ paddingBottom: gadgetkey === 'EventHighlights' ? 6 : undefined }}>
            <CardHeader
                style={{ paddingBottom: 0 }}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Typography variant="h6" component="div">
                            {messages[name] || name}
                        </Typography>
                        <GadgetDateInfo filters={filters} gadgetkey={gadgetkey} />
                    </div>
                }
                action={
                    (() => {
                        const params = new URLSearchParams();
                        params.append('tab', "cases");

                        if (filters?.drivers && filters?.drivers?.length > 0) {
                            const driverIds = filters.drivers.map(driver => driver.driverId);
                            params.append('casesDriverIds', driverIds.join(','));
                        }

                        if (filters?.timeFrame && filters?.rangeDate) {
                            if (filters?.timeFrame !== 'custom') {
                                params.append('range', filters.timeFrame);
                            } else {
                                const startDateParam = filters.rangeDate?.start
                                    ? moment(filters.rangeDate.start).format('YYYY-MM-DD')
                                    : null;
                                const endDateParam = filters.rangeDate?.end
                                    ? moment(filters.rangeDate.end).format('YYYY-MM-DD')
                                    : null;

                                if (startDateParam) {
                                    params.append('startDate', startDateParam);
                                }

                                if (endDateParam) {
                                    params.append('endDate', endDateParam);
                                }
                            }
                        }

                        const queryString = params.toString();
                        switch (gadgetkey) {
                            case 'CasesCoachingSummary':
                                return (
                                    <div className='pending-coaching-case-view-button'>
                                        <a href={`/app/driverPerformance/main?${queryString}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                dispatch(setDriverPerformanceMainTab(3));
                                                navigate(`/app/driverPerformance/main?${queryString}`);
                                            }}
                                        >
                                            {messages['dashboard_view']}
                                        </a>
                                    </div>
                                )
                            case 'EventHighlights':
                                return (
                                    <IconButton
                                        tooltip={messages['menu_settings']}
                                        onClick={() => configHandler(gadgetkey)}
                                        size="small"
                                        icon="tune"
                                        isDisabled={loading}
                                    />
                                )
                        }
                    })()
                }
            />
            <CardContent
                className={`summary-gadget__card-content ${isEventHighlights ? 'summary-gadget__card-content--scrollable' : ''}`}
                style={{
                    maxHeight: isEventHighlights ? '600px' : undefined,
                    overflowY: isEventHighlights ? 'auto' : undefined,
                    padding: isEventHighlights ? '16px' : undefined,
                    display: isEventHighlights ? 'block' : undefined,
                    height: isEventHighlights ? 'auto' : undefined
                }}
            >
                {
                    loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={100} style={{ width: '100%' }}>
                            <Loading show />
                        </Box>
                    ) : viewDataSummary()
                }
            </CardContent>
        </Card>
    );
}

export default SummaryGadget;
